import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import loteService, { ProductoLote } from '../../../../services/loteService';
import LoteAdjuntosPanel from '../../../../components/lotes/LoteAdjuntosPanel';
import { getApiErrorMessage } from '../../../../utils/apiError';
import { formatCantidad } from '../formatCantidad';
import type { ArticuloInfo360Lote } from '../types';

interface Props {
  codigoArticulo: string;
  unidadMedida?: string | null;
  /** Lotes con saldo desde GET info-360 (preferido). */
  lotesDisponibles?: ArticuloInfo360Lote[] | null;
}

function labelEstado(estado: string): { label: string; color: 'success' | 'warning' | 'default' } {
  const e = String(estado ?? '').trim().toUpperCase();
  if (e === 'A') return { label: 'Activo', color: 'success' };
  if (e === 'B') return { label: 'Bloqueado', color: 'warning' };
  return { label: estado || '—', color: 'default' };
}

function saldoLote(lote: ProductoLote): number {
  if (Array.isArray(lote.depositos) && lote.depositos.length > 0) {
    return lote.depositos.reduce((acc, d) => acc + (Number(d.cantidad) || 0), 0);
  }
  return Number(lote.cantidadTotal) || 0;
}

function mapInfo360Lote(l: ArticuloInfo360Lote, codigoArticulo: string): ProductoLote {
  return {
    idLote: l.idLote,
    codigoArticulo,
    nroLote: l.nroLote,
    esSerie: !!l.esSerie,
    fechaVencimiento: l.fechaVencimiento ?? null,
    estado: l.estado || 'A',
    fechaAlta: '',
    cantidadTotal: Number(l.cantidadTotal) || 0,
    depositos: [],
  };
}

function sortLotes(items: ProductoLote[]): ProductoLote[] {
  return items.slice().sort((a, b) => {
    const sa = saldoLote(a);
    const sb = saldoLote(b);
    if (sb !== sa) return sb - sa;
    return String(a.nroLote).localeCompare(String(b.nroLote), 'es');
  });
}

const ArticuloInfo360TabLotes: React.FC<Props> = ({
  codigoArticulo,
  unidadMedida,
  lotesDisponibles,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [lotes, setLotes] = useState<ProductoLote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | false>(false);

  const enriquecerDepositos = useCallback(
    async (base: ProductoLote[], codigo: string) => {
      try {
        const data = await loteService.search({
          codigoArticulo: codigo,
          soloConSaldo: true,
          pageSize: 100,
        });
        const byId = new Map((data.items ?? []).map((l) => [l.idLote, l]));
        const merged = base.map((l) => {
          const full = byId.get(l.idLote);
          if (!full) return l;
          return {
            ...l,
            depositos: full.depositos ?? l.depositos,
            cantidadTotal: full.cantidadTotal ?? l.cantidadTotal,
            fechaVencimiento: full.fechaVencimiento ?? l.fechaVencimiento,
            estado: full.estado || l.estado,
            esSerie: full.esSerie ?? l.esSerie,
            descripcionArticulo: full.descripcionArticulo,
          };
        });
        setLotes(sortLotes(merged));
      } catch {
        /* conservar listado info-360 sin depósitos */
      }
    },
    [],
  );

  const cargar = useCallback(async () => {
    const codigo = String(codigoArticulo ?? '').trim();
    if (!codigo) {
      setLotes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    // Preferir payload de info-360 (API actualizada).
    if (Array.isArray(lotesDisponibles)) {
      const mapped = sortLotes(lotesDisponibles.map((l) => mapInfo360Lote(l, codigo)));
      setLotes(mapped);
      setLoading(false);
      void enriquecerDepositos(mapped, codigo);
      return;
    }

    try {
      const data = await loteService.search({
        codigoArticulo: codigo,
        soloConSaldo: true,
        pageSize: 100,
      });
      setLotes(sortLotes(data.items ?? []));
    } catch (err) {
      const msg = getApiErrorMessage(err, 'No se pudieron cargar los lotes');
      setError(msg);
      setLotes([]);
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [codigoArticulo, lotesDisponibles, enriquecerDepositos, enqueueSnackbar]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Cargando lotes con saldo…
          </Typography>
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => void cargar()} sx={{ textTransform: 'none' }}>
            Reintentar
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  if (lotes.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          Lotes disponibles
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No hay lotes con saldo para este artículo. Podés consultar el maestro de lotes o ingresar stock con
          remito de compra / ajuste.
        </Typography>
        <Button
          component={RouterLink}
          to="/inventario/lotes"
          size="small"
          sx={{ mt: 1, textTransform: 'none' }}
        >
          Ir a Lotes
        </Button>
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Lotes disponibles{unidadMedida ? ` (${unidadMedida})` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {lotes.length} lote{lotes.length === 1 ? '' : 's'} con saldo
          </Typography>
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Lote / serie</TableCell>
                <TableCell>Vencimiento</TableCell>
                <TableCell>Depósitos</TableCell>
                <TableCell align="right">Saldo</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Detalle</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lotes.map((lote) => {
                const est = labelEstado(lote.estado);
                const saldo = saldoLote(lote);
                const deps = (lote.depositos ?? [])
                  .filter((d) => (Number(d.cantidad) || 0) > 0)
                  .map((d) => `${d.nombreUbicacion || d.nroUbicacion}: ${formatCantidad(d.cantidad, unidadMedida)}`)
                  .join(' · ');
                return (
                  <TableRow key={lote.idLote} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {lote.nroLote}
                        </Typography>
                        {lote.esSerie ? <Chip size="small" label="Serie" sx={{ height: 20 }} /> : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {lote.fechaVencimiento ? String(lote.fechaVencimiento).slice(0, 10) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {deps || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCantidad(saldo, unidadMedida)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={est.label} color={est.color} sx={{ height: 22 }} />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={RouterLink}
                        to={`/inventario/lotes/${lote.idLote}`}
                        size="small"
                        endIcon={<OpenInNewIcon fontSize="small" />}
                        sx={{ textTransform: 'none' }}
                      >
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
          <AttachFileIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            Documentos por lote
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Expandí un lote para adjuntar certificados, análisis u otros archivos, o descargar los existentes.
        </Typography>

        {lotes.map((lote) => (
          <Accordion
            key={lote.idLote}
            disableGutters
            elevation={0}
            expanded={expandedId === lote.idLote}
            onChange={(_, open) => setExpandedId(open ? lote.idLote : false)}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px !important',
              mb: 1,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }}>
                <Typography variant="body2" fontWeight={600}>
                  {lote.nroLote}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Saldo {formatCantidad(saldoLote(lote), unidadMedida)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Link
                  component={RouterLink}
                  to={`/inventario/lotes/${lote.idLote}`}
                  underline="hover"
                  variant="caption"
                  onClick={(e) => e.stopPropagation()}
                >
                  Abrir ficha
                </Link>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {expandedId === lote.idLote ? <LoteAdjuntosPanel idLote={lote.idLote} /> : null}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>
    </Stack>
  );
};

export default ArticuloInfo360TabLotes;
